import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError, InteractionStatus, type AccountInfo } from "@azure/msal-browser";
import { loginRequest } from "../../config/msal";
import { useAppDispatch } from "../../store/hooks";
import { setAuth, clearAuth, setMsalInitialized } from "../../store/authSlice";

function readRoles(claims: object): string[] {
  // Entra issues app roles as the `roles` string array and omits the claim when the user has
  // no role assigned - absence means "no roles", any other shape is invalid.
  const roles = (claims as { roles?: unknown }).roles;
  if (roles === undefined) return [];
  if (!Array.isArray(roles) || !roles.every((r) => typeof r === "string")) {
    throw new Error("ID token claim `roles` is not a string array");
  }
  return roles;
}

export default function AuthSync() {
  const { instance, accounts, inProgress } = useMsal();
  const dispatch = useAppDispatch();
  // Errors from the async renewal below are rethrown during render so the error boundary sees them.
  const [error, setError] = useState<Error | null>(null);
  if (error) throw error;

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    const account = accounts[0];
    if (!account) {
      dispatch(clearAuth());
      dispatch(setMsalInitialized());
      return;
    }

    instance.setActiveAccount(account);

    const publish = (a: AccountInfo, claims: object) =>
      dispatch(setAuth({ name: a.name ?? null, username: a.username, roles: readRoles(claims) }));

    if (account.idTokenClaims) {
      publish(account, account.idTokenClaims);
      return;
    }

    // MSAL fills `idTokenClaims` only while the ID token is in the cache. After it expires the
    // account is still signed in through its refresh token, so renew the ID token silently.
    let cancelled = false;
    instance
      .acquireTokenSilent({ ...loginRequest, account })
      .then((result) => {
        if (cancelled) return;
        publish(result.account, result.idTokenClaims);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof InteractionRequiredAuthError) {
          // The refresh token is gone too: the session has ended and the user must sign in again.
          instance.setActiveAccount(null);
          dispatch(clearAuth());
          dispatch(setMsalInitialized());
          return;
        }
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => {
      cancelled = true;
    };
  }, [instance, accounts, inProgress, dispatch]);

  return null;
}
